// src/planner/PlannerPage.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import LoadingSpinner from '../../opencomponents/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import EventGraph from './EventGraph';
import Sidebar from './Sidebar';
import FloatingActionButton from './FloatingActionButton';
import AddEventModal from './AddEventModal';
import {
    PageContainer, PlannerLayout, GraphContainer
} from './plannerStyles';
import { color } from '../../theme/tokens';

// 根事项的区分色：低饱和度，与 V18 暗色基调相容
const generateRandomColor = () => `hsl(${Math.floor(Math.random() * 360)}, 30%, 62%)`;

const assignColorsToRoots = (events) => {
    const colorMap = new Map();
    events.filter(event => !event.parent_id).forEach(root => {
        colorMap.set(root.id, generateRandomColor());
    });
    return colorMap;
};

const getEventColor = (event, events, colorMap) => {
    if (!event.parent_id) return colorMap.get(event.id) || color.text3;
    let current = event;
    while (current.parent_id) {
        const parent = events.find(e => e.id === current.parent_id);
        if (!parent) break;
        current = parent;
    }
    return colorMap.get(current.id) || color.text3;
};

const CARD_WIDTH = 300;
const HORIZONTAL_MARGIN = 50;
const VERTICAL_PADDING = 40;

function PlannerPage() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedNodes, setExpandedNodes] = useState(new Set()); 
    const [colorMap, setColorMap] = useState(new Map());

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [eventToEdit, setEventToEdit] = useState(null);

    const fetchEvents = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiClient.get('/planner/events');
            setEvents(response.data);
            setColorMap(assignColorsToRoots(response.data));
        } catch (error) { console.error("Failed to fetch events:", error); } 
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchEvents(); }, [fetchEvents]);

    const { graphNodes, graphEdges, sidebarEventTrees } = useMemo(() => {
        if (!events.length) return { graphNodes: [], graphEdges: [], sidebarEventTrees: [] };
 
        const eventsWithDate = events.filter(e => e.due_date);
        const sidebarRoots = events.filter(e => !e.parent_id && !e.due_date);
        
        const sidebarTrees = sidebarRoots.map(root => {
            const getAllChildren = (parentId) => events.filter(e => e.parent_id === parentId).map(child => ({ ...child, children: getAllChildren(child.id) }));
            return { ...root, children: getAllChildren(root.id), color: getEventColor(root, events, colorMap) };
        });
 
        eventsWithDate.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const DAY_WIDTH = 250;

        const nodes = [];
        const occupiedRegions = [];
 
        eventsWithDate.forEach(event => {
            const dueDate = new Date(event.due_date);
            if(isNaN(dueDate.getTime())) return;
            
            const diffDays = (dueDate - today) / (86400000);
            const xPos = diffDays * DAY_WIDTH;
            
            const isExpanded = expandedNodes.has(event.id);
            let eventHeight = 80;
            if (isExpanded) {
                const descLines = event.description ? Math.ceil(event.description.length / 50) : 1;
                const undatedCount = events.filter(e => e.parent_id === event.id && !e.due_date).length;
                eventHeight = 160 + (descLines * 20) + (undatedCount * 35);
            }
 
            let yPos = 0, attempts = 0, foundPosition = false;
            while (!foundPosition && attempts < 100) {
                const region = { x1: xPos - HORIZONTAL_MARGIN/2, y1: yPos - VERTICAL_PADDING/2, x2: xPos + CARD_WIDTH + HORIZONTAL_MARGIN/2, y2: yPos + eventHeight + VERTICAL_PADDING/2 };
                const hasOverlap = occupiedRegions.some(r => !(region.x2 <= r.x1 || region.x1 >= r.x2 || region.y2 <= r.y1 || region.y1 >= r.y2));
                if (!hasOverlap) {
                    occupiedRegions.push({ x1: xPos, y1: yPos, x2: xPos + CARD_WIDTH, y2: yPos + eventHeight });
                    foundPosition = true;
                } else {
                    const sign = attempts % 2 === 0 ? 1 : -1;
                    yPos = Math.ceil((attempts + 1) / 2) * 50 * sign;
                    attempts++;
                }
            }
            if (!foundPosition) {
                yPos = (occupiedRegions.length + 1) * 100;
                occupiedRegions.push({ x1: xPos, y1: yPos, x2: xPos + CARD_WIDTH, y2: yPos + eventHeight });
            }
 
            nodes.push({
                id: event.id.toString(),
                position: { x: xPos, y: yPos },
                data: { ...event, color: getEventColor(event, events, colorMap) },
            });
        });
 
        const edges = [];
        nodes.forEach(node => {
            const parentId = node.data.parent_id;
            const parentNode = nodes.find(n => n.id === parentId?.toString());
            if (parentId && parentNode) {
                edges.push({
                    id: `e-${parentId}-${node.id}`,
                    source: parentId.toString(),
                    target: node.id.toString(),
                    color: getEventColor(node.data, events, colorMap)
                });
            }
        });

        return { graphNodes: nodes, graphEdges: edges, sidebarEventTrees: sidebarTrees };
    }, [events, expandedNodes, colorMap]);

    const handleOpenAddModal = (parentId = null) => { setEventToEdit(null); setIsModalOpen(true); };
    const handleOpenEditModal = (event) => { setEventToEdit(event); setIsModalOpen(true); };
    const handleOpenAddSubtaskModal = (parentEvent) => { setEventToEdit({ title: '', description: '', parent_id: parentEvent.id, due_date: null, is_completed: false }); setIsModalOpen(true); };
    const handleCloseModal = () => { setIsModalOpen(false); setEventToEdit(null); };

    const handleSaveEvent = async (formData) => {
        const payload = { ...formData, dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : null };
        try {
            if (eventToEdit && eventToEdit.id) await apiClient.put(`/planner/events/${eventToEdit.id}`, payload);
            else await apiClient.post('/planner/events', payload);
            fetchEvents(); handleCloseModal();
        } catch (error) { console.error("Failed to save event:", error); alert("操作失败。"); }
    };
    
    const handleDeleteEvent = async (eventId) => {
        if (window.confirm("删除此事项及其全部子项？")) {
            try {
                await apiClient.delete(`/planner/events/${eventId}`);
                setExpandedNodes(prev => { const n = new Set(prev); n.delete(eventId); return n; });
                fetchEvents();
            } catch (error) { console.error("Failed to delete:", error); }
        }
    };

    const handleToggleComplete = async (event) => {
         try {
            await apiClient.patch(`/planner/events/${event.id}/toggle`, { isCompleted: !event.is_completed });
            fetchEvents();
        } catch (error) { alert(error.response?.data?.error || '状态更新失败。'); }
    };

    const handleNodeExpand = useCallback((nodeId, isExpanded) => {
        setExpandedNodes(prev => { const n = new Set(prev); isExpanded ? n.add(nodeId) : n.delete(nodeId); return n; });
    }, []);

    if (loading) return <LoadingSpinner />;

    return (
        <PageContainer>
            <PlannerLayout>
                <Sidebar
                    eventTrees={sidebarEventTrees}
                    allEvents={events}
                    expandedNodes={expandedNodes}
                    onNodeExpand={handleNodeExpand}
                    onEdit={handleOpenEditModal}
                    onDelete={handleDeleteEvent}
                    onAddSubtask={handleOpenAddSubtaskModal}
                    onToggleComplete={handleToggleComplete}
                />
                <GraphContainer>
                    <EventGraph
                        nodes={graphNodes}
                        edges={graphEdges}
                        allEvents={events}
                        expandedNodes={expandedNodes}
                        onNodeExpand={handleNodeExpand}
                        onEdit={handleOpenEditModal}
                        onDelete={handleDeleteEvent}
                        onAddSubtask={handleOpenAddSubtaskModal}
                        onToggleComplete={handleToggleComplete}
                    />
                </GraphContainer>
            </PlannerLayout>
            <FloatingActionButton onClick={() => handleOpenAddModal()}/>
            <AddEventModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleSaveEvent}
                eventToEdit={eventToEdit}
                allEvents={events}
            />
        </PageContainer>
    );
}

export default PlannerPage;